/**
 * quizReviewViewModel — la relecture du quiz de prérequis en props de `<QuizReview>`, dans la
 * feuille de Sam posée sur l'activité.
 *
 * Deux ouvertures (`quizReview` du moteur) : la fin du quiz, qui finit par « Continuer » (l'activité
 * s'ouvre), et « Revoir le quiz », qui n'a que sa croix, comme l'ancienne boîte.
 *
 * Pur : les gestes sont au crochet de la relecture.
 */
import {
    type QuizReview,
    type QuizReviewQuestion,
    type QuizReviewState,
} from "../../../../../engine/activity/prerequisite/quizReview"
import { quizOptionMark } from "../../../ui/formatters/quizReviewFormatter"
import { type UIQuizReview, type UIQuizReviewQuestion } from "../../../ui/types/studentQuizReview"
import { quizTextFormat } from "./quizTextFormat"

export const QUIZ_REVIEW_TEXTS = {
    /** Le bouton de l'ancienne correction, sous la fin du quiz. */
    continue: "Continuer",
}

export type QuizReviewVM = {
    /** La correction du quiz est ouverte. */
    open: boolean
    review: UIQuizReview | null
    loading: boolean
    error: boolean
    /** À la fin du quiz : « Continuer ». */
    continueLabel: string | null
}

export function toUIQuizReview(review: QuizReview): UIQuizReview {
    return {
        score: review.score,
        total: review.total,
        percentage: review.percentage,
        passed: review.passed,
        questions: review.questions.map(toUIQuizReviewQuestion),
    }
}

/** Une question relue, celle du quiz de prérequis comme celle du quiz de fin de séance. */
export function toUIQuizReviewQuestion(question: QuizReviewQuestion): UIQuizReviewQuestion {
    const labels = question.displayOptions ?? question.options
    const answerRank = question.answer != null ? question.options.indexOf(question.answer) : -1
    // La réponse comme l'élève l'a vue dans le quiz : avec ses formules.
    const answer = answerRank >= 0 ? (labels[answerRank] ?? question.answer) : question.answer
    return {
        id: question.id,
        text: question.text,
        // Le rendu riche (une WebView sur téléphone) seulement pour une question à formule.
        textFormat: quizTextFormat(question.text),
        answer,
        ...(answer != null && quizTextFormat(answer) === "rich" ? { answerFormat: "rich" as const } : {}),
        result: question.result,
        // Montrée avec ses formules, marquée sur son texte simple : celui que l'élève a répondu.
        options: question.options.map((plain, rank) => {
            const label = labels[rank] ?? plain
            return {
                label,
                mark: quizOptionMark(plain, question),
                ...(quizTextFormat(label) === "rich" ? { labelFormat: "rich" as const } : {}),
            }
        }),
    }
}

export function quizReviewViewModel(state: Pick<QuizReviewState, "open" | "status" | "review">): QuizReviewVM {
    return {
        open: state.open != null,
        review: state.status === "ready" && state.review != null ? toUIQuizReview(state.review) : null,
        loading: state.status === "loading",
        error: state.status === "error",
        continueLabel: state.open === "attempt" ? QUIZ_REVIEW_TEXTS.continue : null,
    }
}
